import React from 'react'
import { Select, Icon, Tooltip, Tag } from 'antd'
import moment from 'moment'

const { Option } = Select

// single source of truth for "has this meeting's date already passed" — used to decide
// whether a meeting can be documented (AgentMeetings) and which history bucket it falls
// into (MentorsMeetings dashboard). Date-only comparison: a meeting today counts as past
// once the day is over, regardless of what hour it was scheduled for.
export const isMeetingPast = (meetingTime: string, now: moment.Moment = moment()) =>
    moment(meetingTime)
        .startOf('day')
        .isBefore(now.clone().startOf('day'))

// a meeting can be marked occurred/not-occurred from the start of its own day — a coordinator
// sitting with the agent at 09:00 documents a 17:00 meeting the same day. Date-only, so only
// a meeting on a later day is still too early to document.
export const isMeetingDocumentable = (meetingTime: any, now: moment.Moment = moment()) =>
    !!meetingTime &&
    moment(meetingTime)
        .startOf('day')
        .isSameOrBefore(now.clone().startOf('day'))

// the meetings feature went live on this date — there is nothing to show before it, so every
// picker blocks earlier dates. Built per call: a module-level moment would be mutable shared state
export const meetingsStartDate = () => moment('2026-08-01', 'YYYY-MM-DD')

// for the day pickers — anything before the cutoff day
export const isBeforeMeetingsStart = (current: any) =>
    !!current &&
    moment(current)
        .startOf('day')
        .isBefore(meetingsStartDate().startOf('day'))

// for the month pickers — August 2026 (the cutoff month) stays selectable, only earlier months are blocked
export const isBeforeMeetingsStartMonth = (current: any) =>
    !!current &&
    moment(current)
        .startOf('month')
        .isBefore(meetingsStartDate().startOf('month'))

// shared by AgentMeetings/MentorsMeetings history and the undocumented-meetings table:
// blue "עתידית" tag with a tooltip explaining why, wherever a meeting is in the future
export const FutureMeetingTag = () => (
    <Tooltip title="זמן הפגישה טרם הגיע">
        <Tag color="blue" style={{ marginInlineStart: 8 }}>
            עתידית
        </Tag>
    </Tooltip>
)

export const STATUS_LABELS: { [key: string]: string } = {
    occurred: 'התקיימה',
    not_occurred: 'לא התקיימה',
    not_documented: 'לא תועדה'
}

// solid fill (not antd's outlined Tag look) so status reads as a different tag category than the outlined "עתידית" tag
export const STATUS_BADGE: { [key: string]: { background: string; color: string } } = {
    occurred: { background: '#52c41a', color: 'white' },
    not_occurred: { background: '#ff4d4f', color: 'white' },
    not_documented: { background: '#f0f0f0', color: '#8c8c8c' }
}

// only these two are real choices; not_documented is the default state for a meeting nobody has marked yet, never something a user picks
export const OCCURRED_CHOICES = ['occurred', 'not_occurred']

const statusBadgeStyle = {
    display: 'inline-block',
    paddingInline: '10px',
    height: '22px',
    lineHeight: '22px',
    fontSize: '14px',
    borderRadius: '4px'
}

interface RenderStatusCellArgs {
    status: string
    recordId: number
    editingMeetingId: number | null
    isFuture?: boolean
    onEdit: (id: number) => void
    onCancelEdit: () => void
    onChange: (status: string) => void
}

// shared by AgentMeetings and MentorsMeetings: badge + pencil, swaps to an inline Select when editing
export const renderStatusCell = ({
    status,
    recordId,
    editingMeetingId,
    isFuture,
    onEdit,
    onCancelEdit,
    onChange
}: RenderStatusCellArgs) => {
    const displayStatus = status || 'not_documented'
    const badge = (
        <span style={{ ...statusBadgeStyle, ...STATUS_BADGE[displayStatus] }}>{STATUS_LABELS[displayStatus]}</span>
    )

    if (!isFuture && editingMeetingId === recordId) {
        return (
            <Select
                autoFocus
                defaultOpen
                value={OCCURRED_CHOICES.includes(status) ? status : undefined}
                placeholder="בחירת סטטוס"
                style={{ width: '100%' }}
                onChange={onChange}
                onBlur={onCancelEdit}
            >
                {OCCURRED_CHOICES.map(key => (
                    <Option key={key} value={key}>
                        {STATUS_LABELS[key]}
                    </Option>
                ))}
            </Select>
        )
    }

    const pencil = (
        <Icon
            type="edit"
            style={{
                marginInlineStart: '5px',
                color: isFuture ? '#bfbfbf' : '#1890ff',
                cursor: isFuture ? 'not-allowed' : 'pointer'
            }}
            onClick={() => !isFuture && onEdit(recordId)}
        />
    )

    return (
        <span>
            {badge}
            {isFuture ? <Tooltip title="ניתן לסמן רק אחרי מועד הפגישה">{pencil}</Tooltip> : pencil}
        </span>
    )
}
